import { useState } from 'react';
import dynamic from 'next/dynamic';
import Script from 'next/script';

//hooks
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { post } from '@/utils/client';
import useToggle from '@/utils/hooks/utils/useToggle';

// Routes
import { ZAPIER_PUBLIC_TOKEN } from '@/lib/routes/apiEndpoints';

import Button from '@/components/base/Button';
import H2 from '@/components/base/Headings/H2';
//Components
import { ZapierIcon } from '@/components/icons/Integrations/Zapier';
import { ErrorBanner } from '@/components/pages/settings/integrations/ZapierIntegration/ErrorBanner';
const ZapierTokenModal = dynamic(
    () => import('@/components/pages/settings/integrations/Modals/ZapierTokenModal')
);

export function ZapierIntegration() {
    const { t } = useTranslation('integrations');
    const { user } = useUser();
    const { isOpen, toggle } = useToggle();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [globalError, setGlobalError] = useState<string>(null);
    const [dataSuccess, setDataSuccess] = useState<{ token: string }>(null);

    const handleGenerateZapierToken = async () => {
        setGlobalError(null);
        setIsLoading(true);
        try {
            const result: { data: { token: string } } = await post(ZAPIER_PUBLIC_TOKEN);
            setDataSuccess({ token: result.data.token });
            toggle();
        } catch (e) {
            // Display global error
            setGlobalError(t('zapier.error_while_generating_key'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {globalError ? (
                <ErrorBanner error={globalError} closeBanner={() => setGlobalError(null)} />
            ) : null}
            <div className="grid h-full w-full grid-cols-[160px_minmax(0px,_1fr)] gap-10">
                <div className="hidden flex-col @2xl:col-span-1 @2xl:flex">
                    <div className="flex h-40 w-40 items-center justify-center rounded-xl bg-icoGray-100 dark:bg-icoDarkMode-beton">
                        <ZapierIcon width={110} />
                    </div>
                </div>
                <div className="col-span-full flex flex-1 flex-col space-y-10 @2xl:col-span-1">
                    <div className="flex flex-col space-y-5">
                        <div className="flex items-center justify-between">
                            <H2>Zapier</H2>
                            <Button
                                onClick={handleGenerateZapierToken}
                                isLoading={isLoading}
                                data-testid="zapier-key-generation-button">
                                {t('zapier.generate_key')}
                            </Button>
                        </div>

                        <span className="font-GMedium text-icoGray-500 dark:text-icoDarkMode-wolf">
                            {t('overview.zapier_description')}
                        </span>
                    </div>
                    <div className="zapier-wrapper">
                        {/* @ts-ignore */}
                        <zapier-full-experience
                            sign-up-email={user?.email}
                            sign-up-first-name={user?.first_name}
                            sign-up-last-name={user?.last_name}
                            client-id={process.env.NEXT_PUBLIC_ZAPIER_CLIENT_ID}
                            theme="light"
                            app-search-bar-display="show"
                        />
                    </div>
                </div>
            </div>
            <ZapierTokenModal isOpen={isOpen} toggle={toggle} data={dataSuccess} />
            <Script
                type="module"
                src="https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.esm.js"
            />
            <link
                rel="stylesheet"
                href="https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.css"
            />
        </>
    );
}
